import { useState } from 'react';
import { Group } from '@mantine/core';
import { ThemedText } from '../../Typography';
import DarkModal from '../Modals/DarkModal';
import ThemedButton from './ThemedButton';
import ThemedBadge from '../Badges/ThemedBadge';
import { COLORS } from '../../../src/constants/theme';
import { themeVar } from '../../../src/utils/pageTheme';
import { showNotification } from '@mantine/notifications';
import { IconCheck, IconX, IconBell, IconBellRinging } from '@tabler/icons-react';
import { useUser } from '../../../src/context/UserContext'; // adjust the import path as needed
import { useIsMobile } from '../../../src/context/MediaQueryContext';

/**
 * The unsubscribed fill. It starts from the white-text fill, like
 * ThemedButton's filled variant, so a light accent (night-flow's gold) deepens
 * until the white label reads (#2706). Outside a themed page it's the house
 * orange, as before.
 */
export const SUBSCRIBE_FILL = `linear-gradient(135deg, ${themeVar('white-text-fill')} 0%, color-mix(in srgb, ${themeVar('white-text-fill')} 84%, black) 100%)`;

interface SubscribeButtonProps {
  entityType: 'artist' | 'organizer' | 'area';
  entitySlug: string;
  entityName: string;
  isSubscribed: boolean;
  initialSubscribersCount?: number;
  onSubscribedChange?: (newValue: boolean) => void;
  onSubscribersCountChange?: (count: number) => void;
  fullWidth?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Override the state-based variant. Default: 'filled' when unsubscribed, 'light' when subscribed. */
  variant?: 'filled' | 'light';
}

const SubscribeButton: React.FC<SubscribeButtonProps> = ({
  entityType,
  entitySlug,
  entityName,
  isSubscribed,
  initialSubscribersCount,
  onSubscribedChange,
  onSubscribersCountChange,
  fullWidth,
  size = 'sm',
  variant,
}) => {
  const { user } = useUser();
  const isMobile = useIsMobile();
  const [openModal, setOpenModal] = useState(false);
  const [subscribed, setSubscribed] = useState(isSubscribed);
  const [subscribersCount, setSubscribersCount] = useState(initialSubscribersCount || 0);

  const endpointBase =
    entityType === 'artist'
      ? `/artists/${entitySlug}`
      : entityType === 'organizer'
        ? `/o/${entitySlug}`
        : `/city/${entitySlug}`;

  const handleSubscribe = async () => {
    if (!user) {
      showNotification({
        color: COLORS.orange,
        title: 'Login Required',
        message: 'You must be logged in to subscribe. Redirecting you to login...',
        icon: <IconX />,
      });

      setTimeout(() => {
        window.location.href = `/login?return_to=${window.location.pathname}`;
      }, 1500);

      return;
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    const endpoint = `${endpointBase}/${subscribed ? 'unsubscribe' : 'subscribe'}`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok) {
        setSubscribed(data.is_subscribed);
        setSubscribersCount(data.subscribers_count);
        onSubscribersCountChange?.(data.subscribers_count);
        onSubscribedChange?.(data.is_subscribed);

        showNotification({
          color: 'green',
          title: data.is_subscribed ? 'Subscribed' : 'Unsubscribed',
          message: data.message,
          icon: <IconCheck />,
        });
      } else {
        throw new Error(data.message || 'Failed to process');
      }
    } catch (error: any) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: error.message || 'Something went wrong.',
        icon: <IconX />,
      });
    } finally {
      setOpenModal(false);
    }
  };

  return (
    <>
      <ThemedButton
        {...(fullWidth ? { fullWidth: true } : {})}
        variant={variant ?? (subscribed ? 'light' : 'filled')}
        size={size}
        fw={600}
        ff="Poppins, sans-serif"
        onClick={() => setOpenModal(true)}
        leftSection={
          subscribed ? (
            <IconBellRinging size={isMobile ? 16 : 18} />
          ) : (
            <IconBell size={isMobile ? 16 : 18} />
          )
        }
        style={{
          background: variant === undefined && !subscribed ? SUBSCRIBE_FILL : undefined,
          transition: 'all 0.2s ease',
        }}
      >
        <Group gap="xs" justify="center" align="center" wrap="nowrap">
          <span>{subscribed ? 'Subscribed' : 'Subscribe'}</span>
          {subscribersCount > 0 && (
            <ThemedBadge
              variant="filled"
              size="lg"
              style={{
                fontSize: '0.6rem',
                minHeight: '20px',
                padding: '0 8px',
                height: '20px',
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '24%',
                lineHeight: 0.8,
              }}
            >
              {subscribersCount}
            </ThemedBadge>
          )}
        </Group>
      </ThemedButton>

      <DarkModal
        opened={openModal}
        onClose={() => setOpenModal(false)}
        title={subscribed ? 'Confirm Unsubscribe' : 'Confirm Subscribe'}
      >
        <ThemedText mt={16}>
          Are you sure you want to {subscribed ? 'unsubscribe from' : 'subscribe to'} <strong>{entityName}</strong>?
        </ThemedText>
        <ThemedText size="sm" variant="dimmed" mb={16}>
          {subscribed
            ? `You will no longer receive a weekly email with upcoming events from this ${entityType === 'area' ? 'city' : entityType}.`
            : `You will receive a weekly email every Sunday with upcoming events from this ${entityType === 'area' ? 'city' : entityType}.`}
        </ThemedText>
        <Group justify="flex-end">
          <ThemedButton variant="filled" color="gray" onClick={() => setOpenModal(false)}>
            Cancel
          </ThemedButton>
          <ThemedButton color={subscribed ? 'red' : COLORS.orange} onClick={handleSubscribe}>
            {subscribed ? 'Unsubscribe' : 'Subscribe'}
          </ThemedButton>
        </Group>
      </DarkModal>
    </>
  );
};

export default SubscribeButton;
